import { normalizeMultiFilterValues } from "@sd/lib/ticketFilterApi";
import { looksLikeOpaqueUserId } from "@sd/lib/ticketRowMapping";

export type PtsDropdownOption = { value: string; label: string };

export type PtsProjectNode = {
  id: string;
  name: string;
  children: PtsProjectNode[];
};

/** Keep a saved edit value visible when it is missing from the fetched open list. */
export function ensurePtsSelectedOption(
  options: PtsDropdownOption[],
  selectedValue: string,
  fallbackLabel = "Selected"
): PtsDropdownOption[] {
  const value = selectedValue.trim();
  if (!value) {
    return options;
  }
  if (options.some(option => option.value === value)) {
    return options;
  }
  return [{ value, label: fallbackLabel }, ...options];
}

/** Status UUID sent when creating a PTS sub-task from the end-user portal. */
export const PTS_PORTAL_SUBTASK_CREATE_STATUS_ID =
  "fb1946dc-c6e8-4caf-87d5-d1431c24e5c6";

/**
 * Extract array from PTS API response.
 * Response shape: { "success": true, "project": [...] }
 */
function extractPtsArray(payload: unknown): unknown[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== "object") return [];

  const o = payload as Record<string, unknown>;

  // PTS open-project-list returns { project: [...] }
  if (Array.isArray(o.project)) return o.project;

  // Generic fallbacks
  const fallbackKeys = ["results", "data", "list", "items", "records"];
  for (const k of fallbackKeys) {
    if (Array.isArray(o[k])) return o[k] as unknown[];
  }

  return [];
}

/** Map PTS project list API payload `{ project: [...] }` to `{ value, label }`. */
export function ptsDropdownOptionsFromPayload(
  payload: unknown
): PtsDropdownOption[] {
  const out: PtsDropdownOption[] = [];

  for (const raw of extractPtsArray(payload)) {
    if (!raw || typeof raw !== "object") continue;

    const row = raw as Record<string, unknown>;
    const value = String(row.id ?? "").trim();
    const label = String(
      row.projectname ?? row.project_name ?? row.name ?? ""
    ).trim();

    if (
      !value ||
      !label ||
      label === "null" ||
      label === value ||
      looksLikeOpaqueUserId(label)
    ) {
      continue;
    }

    out.push({ value, label });
  }

  return out.sort((a, b) => a.label.localeCompare(b.label));
}

function ptsProjectNameFromRow(row: Record<string, unknown>): string {
  return String(
    row.projectname ?? row.project_name ?? row.name ?? ""
  ).trim();
}

function ptsParentRefFromRow(
  row: Record<string, unknown>
): { id: string; name: string } | null {
  const raw =
    row.parent ??
    row.parent_id ??
    row.parentid ??
    row.parent_project ??
    row.parentproject;
  if (raw == null || raw === "" || raw === false) return null;
  if (typeof raw === "string" || typeof raw === "number") {
    const id = String(raw).trim();
    return id ? { id, name: "" } : null;
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const id = String(o.id ?? o.uuid ?? "").trim();
    if (!id) return null;
    return { id, name: ptsProjectNameFromRow(o) };
  }
  return null;
}

function sortPtsProjectTree(nodes: PtsProjectNode[]) {
  nodes.sort((a, b) => a.name.localeCompare(b.name));
  for (const node of nodes) sortPtsProjectTree(node.children);
}

/** Group open-project-list rows into parent/child cards for the Projects page. */
export function ptsProjectTreeFromPayload(payload: unknown): PtsProjectNode[] {
  type Draft = PtsProjectNode & { parentId: string };
  const nodes = new Map<string, Draft>();

  for (const raw of extractPtsArray(payload)) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const id = String(row.id ?? "").trim();
    const name = ptsProjectNameFromRow(row);
    if (
      !id ||
      !name ||
      name === "null" ||
      name === id ||
      looksLikeOpaqueUserId(name)
    ) {
      continue;
    }
    const parent = ptsParentRefFromRow(row);
    nodes.set(id, {
      id,
      name,
      children: [],
      parentId: parent?.id && parent.id !== id ? parent.id : "",
    });
  }

  for (const raw of extractPtsArray(payload)) {
    if (!raw || typeof raw !== "object") continue;
    const parent = ptsParentRefFromRow(raw as Record<string, unknown>);
    if (!parent?.id || nodes.has(parent.id)) continue;
    const parentName = parent.name.trim();
    if (
      !parentName ||
      parentName === "null" ||
      looksLikeOpaqueUserId(parentName)
    ) {
      continue;
    }
    nodes.set(parent.id, {
      id: parent.id,
      name: parentName,
      children: [],
      parentId: "",
    });
  }

  const roots: PtsProjectNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  sortPtsProjectTree(roots);
  return roots;
}

/** Merge selected project ids into options so filters never show raw UUIDs. */
export function projectFilterOptionsWithSelection(
  payload: unknown,
  selectedIds: string[],
  fallbackLabel = "Selected project"
): PtsDropdownOption[] {
  let options = ptsDropdownOptionsFromPayload(payload);
  for (const id of normalizeMultiFilterValues(selectedIds)) {
    options = ensurePtsSelectedOption(options, id, fallbackLabel);
  }
  return options;
}

/**
 * Map PTS work type API payload (direct array) to `{ value, label }`.
 * Response: [{ "value": "...", "label": "...", "is_active": true }]
 */
export function ptsWorkTypeOptionsFromPayload(payload: unknown): PtsDropdownOption[] {
  const arr = Array.isArray(payload) ? payload : [];
  return arr
    .filter((r: any) => r?.is_active !== false && r?.value && r?.label)
    .map((r: any) => ({ value: String(r.value), label: String(r.label) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Map PTS task list API payload `{ tasks: [...] }` to `{ value, label }`.
 * Response: { "success": true, "tasks": [{ "id": "...", "taskname": "..." }] }
 */
export function ptsTaskOptionsFromPayload(payload: unknown): PtsDropdownOption[] {
  if (!payload || typeof payload !== "object") return [];

  const o = payload as Record<string, unknown>;
  const arr = Array.isArray(o.tasks)
    ? o.tasks
    : Array.isArray(o.task)
    ? o.task
    : Array.isArray(o.data)
    ? o.data
    : Array.isArray(o.results)
    ? o.results
    : [];

  const out: PtsDropdownOption[] = [];

  for (const raw of arr) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const value = String(row.id ?? "").trim();
    const label = String(row.taskname ?? row.task_name ?? row.name ?? value).trim();
    if (value && label && label !== "null") {
      out.push({ value, label });
    }
  }

  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/** Resolve parent task status UUID from task list payload by selected task id. */
export function ptsTaskStatusById(
  payload: unknown,
  taskId: string
): string | undefined {
  if (!payload || typeof payload !== "object" || !taskId) return undefined;

  const o = payload as Record<string, unknown>;
  const arr = Array.isArray(o.tasks) ? o.tasks : [];

  for (const raw of arr) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    if (String(row.id ?? "") === taskId) {
      const status = row.status;
      return status != null && String(status).trim() ? String(status).trim() : undefined;
    }
  }

  return undefined;
}
